import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, OverlayTrigger, Popover, Spinner } from "react-bootstrap";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const roundToTen = (value) => Math.round(value / 10) * 10;

const AdvisorHelp = () => (
    <OverlayTrigger
        trigger="click"
        placement="top"
        rootClose
        overlay={(
            <Popover style={{ maxWidth: "520px" }}>
                <Popover.Header as="h6">How to use Price Advisor</Popover.Header>
                <Popover.Body className="text-muted" style={{ fontSize: "0.875rem" }}>
                    <p className="mb-2">
                        The scale compares your current assignment price with similar open offers.
                        Drag the slider to adjust the Speed-Up bonus and make the offer more competitive.
                    </p>
                    <p className="mb-2">
                        A price close to the <strong>minimum</strong> may stay in the queue longer,
                        especially when many similar offers are available.
                    </p>
                    <p className="mb-0">
                        A price around or slightly above the <strong>average</strong> usually gives a
                        good balance between cost and speed. The upper range is fastest, but may overpay.
                    </p>
                </Popover.Body>
            </Popover>
        )}
    >
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-circle d-inline-flex align-items-center justify-content-center"
            style={{ width: 22, height: 22, padding: 0, lineHeight: 1 }}
            aria-label="Show price advisor help"
        >
            ?
        </button>
    </OverlayTrigger>
);

const AssignmentPriceAdvisor = ({
                                    visible,
                                    assignmentName,
                                    currentPriceVirt,
                                    stats,
                                    isLoading = false,
                                    error = "",
                                    speedUpOptions = [],
                                    currentSpeedUp = 0,
                                    onSpeedUpChange,
                                }) => {
    const min = stats?.minPrice ?? null;
    const avg = stats?.avgPrice ?? null;
    const max = stats?.maxPrice ?? null;
    const sampleCount = stats?.sampleCount ?? null;
    const hasData = min != null && avg != null && max != null;
    const shouldRender = visible && hasData && sampleCount && sampleCount > 3;

    const canAdjustSpeedUp =
        shouldRender &&
        Array.isArray(speedUpOptions) &&
        speedUpOptions.length > 1 &&
        typeof onSpeedUpChange === "function" &&
        currentPriceVirt != null &&
        Number.isFinite(Number(currentPriceVirt)) &&
        max > min;
    const [draftTargetPrice, setDraftTargetPrice] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (!isDragging) {
            setDraftTargetPrice(null);
        }
    }, [currentPriceVirt, isDragging]);

    const baseWithoutSpeedUp = Number(currentPriceVirt || 0) - Number(currentSpeedUp || 0);
    const maxSpeedUp = Array.isArray(speedUpOptions) && speedUpOptions.length
        ? Math.max(...speedUpOptions.map(option => Number(option.sum || 0)))
        : 7000;
    const draftSpeedUp = useMemo(() => {
        if (!canAdjustSpeedUp || draftTargetPrice == null) return null;

        return clamp(
            roundToTen(Number(draftTargetPrice) - baseWithoutSpeedUp),
            0,
            maxSpeedUp
        );
    }, [baseWithoutSpeedUp, canAdjustSpeedUp, draftTargetPrice, maxSpeedUp]);
    const displayPriceVirt = draftSpeedUp != null
        ? Math.round(baseWithoutSpeedUp + Number(draftSpeedUp || 0))
        : Number(currentPriceVirt);
    const activeSliderValue = canAdjustSpeedUp
        ? clamp(Number(draftTargetPrice ?? displayPriceVirt), Number(min), Number(max))
        : Number(displayPriceVirt || 0);

    let userPercent = null;
    let adviceText = "";
    let adviceTone = "";

    if (displayPriceVirt != null && Number.isFinite(displayPriceVirt) && max > min) {
        const clamped = clamp(displayPriceVirt, min, max);
        const range = max - min;
        const ratio = (clamped - min) / range;
        userPercent = ratio * 100;

        const avgRatio = (avg - min) / range;
        const highStartRatio = 0.75;

        if (ratio < avgRatio) {
            adviceTone = "low";
            adviceText = "Below most offers. Raise bonus to speed up.";
        } else if (ratio >= avgRatio && ratio < highStartRatio) {
            adviceTone = "good";
            adviceText = "Competitive range. Looks good.";
        } else {
            adviceTone = "high";
            adviceText = "Top range. Attractive, but maybe overpaying.";
        }
    }

    let labelTransform = "translateX(-50%)";
    if (userPercent !== null) {
        if (userPercent < 10) {
            labelTransform = "translateX(0)";
        } else if (userPercent > 85) {
            labelTransform = "translateX(-100%)";
        }
    }

    const commitSpeedUpChange = () => {
        if (!canAdjustSpeedUp) return;
        const speedUpToApply = draftSpeedUp;

        if (speedUpToApply != null && Number(speedUpToApply || 0) !== Number(currentSpeedUp || 0)) {
            onSpeedUpChange(Number(speedUpToApply || 0));
        }

        setIsDragging(false);
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <Card className="border border-secondary small h-100" style={{ minWidth: 0 }}>
            <Card.Header className="py-1 bg-secondary bg-card-header text-white fw-semibold">
                <div className="d-flex justify-content-between align-items-center mb-0 gap-2">
                    <div className="d-inline-flex align-items-center gap-2">
                        <h6 className="mb-0 text-white d-none d-md-block">Price Advisor</h6>
                        <h6 className="mb-0 text-white d-block d-md-none">Price</h6>
                        <AdvisorHelp />
                    </div>
                    {assignmentName && (
                        <span className="badge bg-light text-black border">
                            {assignmentName}
                        </span>
                    )}
                </div>
            </Card.Header>
            <Card.Body className="py-2 px-3">
                {isLoading && (
                    <div className="d-flex align-items-center gap-2 text-muted small">
                        <Spinner animation="border" size="sm" />
                        <span>Loading price statistics...</span>
                    </div>
                )}

                {!isLoading && error && (
                    <Alert variant="warning" className="py-1 px-2 mb-2">
                        <small>{error}</small>
                    </Alert>
                )}

                {!isLoading && !error && (
                    <>
                        <div className="mb-2">
                            <div
                                className="position-relative"
                                style={{ paddingTop: "28px", paddingBottom: "6px" }}
                            >
                                {userPercent != null && displayPriceVirt != null && (
                                    <div
                                        className="position-absolute text-center"
                                        style={{
                                            left: `${userPercent}%`,
                                            top: "2px",
                                            transform: labelTransform,
                                            fontSize: "11px",
                                            pointerEvents: "none",
                                            whiteSpace: "nowrap",
                                            textAlign:
                                                userPercent < 10
                                                    ? "left"
                                                    : userPercent > 90
                                                        ? "right"
                                                        : "center",
                                        }}
                                    >
                                        <span className="text-muted">Price: </span>
                                        <strong>{displayPriceVirt}V</strong>
                                    </div>
                                )}

                                <div
                                    className="progress position-relative"
                                    style={{ height: "6px", overflow: "visible" }}
                                >
                                    <div className="progress-bar bg-secondary" role="progressbar" style={{ width: "50%" }} />
                                    <div className="progress-bar bg-info" role="progressbar" style={{ width: "25%" }} />
                                    <div className="progress-bar bg-warning" role="progressbar" style={{ width: "25%" }} />

                                    {canAdjustSpeedUp ? (
                                        <>
                                            <style>{`
                                                .vl-price-advisor-range {
                                                    -webkit-appearance: none;
                                                    appearance: none;
                                                    background: transparent;
                                                    cursor: pointer;
                                                    height: 20px;
                                                    left: 0;
                                                    top: 50%;
                                                    transform: translateY(-50%);
                                                    z-index: 3;
                                                }
                                                .vl-price-advisor-range::-webkit-slider-runnable-track {
                                                    background: transparent;
                                                    border: 0;
                                                    height: 6px;
                                                }
                                                .vl-price-advisor-range::-webkit-slider-thumb {
                                                    -webkit-appearance: none;
                                                    appearance: none;
                                                    width: 16px;
                                                    height: 16px;
                                                    margin-top: -5px;
                                                    border-radius: 50%;
                                                    border: 4px solid #198754;
                                                    background: #fff;
                                                    box-sizing: border-box;
                                                }
                                                .vl-price-advisor-range::-moz-range-track {
                                                    background: transparent;
                                                    border: 0;
                                                    height: 6px;
                                                }
                                                .vl-price-advisor-range::-moz-range-thumb {
                                                    width: 16px;
                                                    height: 16px;
                                                    border-radius: 50%;
                                                    border: 4px solid #198754;
                                                    background: #fff;
                                                    box-sizing: border-box;
                                                }
                                            `}</style>
                                            <input
                                                type="range"
                                                className="position-absolute w-100 vl-price-advisor-range"
                                                min={min}
                                                max={max}
                                                step="10"
                                                value={activeSliderValue}
                                                onMouseDown={() => setIsDragging(true)}
                                                onTouchStart={() => setIsDragging(true)}
                                                onChange={(event) => setDraftTargetPrice(Number(event.target.value))}
                                                onMouseUp={commitSpeedUpChange}
                                                onTouchEnd={commitSpeedUpChange}
                                                onBlur={commitSpeedUpChange}
                                                onKeyUp={commitSpeedUpChange}
                                                aria-label="Adjust Speed-Up Bonus"
                                            />
                                        </>
                                    ) : userPercent != null && (
                                        <div
                                            className="position-absolute d-flex justify-content-center"
                                            style={{
                                                left: `${userPercent}%`,
                                                top: "50%",
                                                transform: "translate(-50%, -50%)",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "16px",
                                                    height: "16px",
                                                    borderRadius: "50%",
                                                    border: "4px solid #198754",
                                                    backgroundColor: "#fff",
                                                    boxSizing: "border-box",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-between text-muted mt-1" style={{ fontSize: "12px" }}>
                                <div className="text-start">
                                    <div>Min</div>
                                    <strong>{`${min} Virt`}</strong>
                                </div>
                                <div className="text-center">
                                    <div>Avg</div>
                                    <strong>{`${avg} Virt`}</strong>
                                </div>
                                <div className="text-end">
                                    <div>Max</div>
                                    <strong>{`${max} Virt`}</strong>
                                </div>
                            </div>
                        </div>

                        {adviceText && (
                            <div
                                className="mb-0 small text-truncate d-block"
                                title={`${adviceTone === "low" ? "Price is lower than most offers: " : ""}${adviceTone === "good" ? "Price looks great: " : ""}${adviceTone === "high" ? "Price is above average: " : ""}${adviceText}`}
                                style={{
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0
                                }}
                            >
                                <span
                                    className={
                                        adviceTone === "low" || adviceTone === "high"
                                            ? "text-danger fw-semibold"
                                            : "text-success fw-semibold"
                                    }
                                >
                                    {adviceTone === "low" && "Low price: "}
                                    {adviceTone === "good" && "Price looks great: "}
                                    {adviceTone === "high" && "Price is above average: "}
                                </span>
                                <span className="text-muted">{adviceText}</span>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default AssignmentPriceAdvisor;
